/**
 * Centralised API helper.
 *
 * Web (default): same-origin requests with the `session_token` cookie
 * sent via `credentials: 'include'`. No URL prefix needed because the
 * web app is served from the same domain as the API ingress.
 *
 * Native (Capacitor): the WebView is served from `capacitor://localhost`
 * which is a different origin from the backend. Cookies don't reliably
 * persist there, so we:
 *   1. Prefix every `/api/...` path with the absolute backend URL
 *      (REACT_APP_BACKEND_URL).
 *   2. Add `Authorization: Bearer <token>` from native storage so the
 *      backend can authenticate without a cookie.
 *   3. Tag the request with `X-Client: capacitor` so login endpoints
 *      know to return the token in the JSON body.
 */
import { isNative, getStoredToken } from './native';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Online/offline state — call sites can check isOnline() before making requests.
let _isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { _isOnline = true; });
  window.addEventListener('offline', () => { _isOnline = false; });
}
export const isOnline = () => _isOnline;

/**
 * Build the absolute URL for an API path.
 * - On native: always prepend BACKEND_URL.
 * - On web: prepend BACKEND_URL when set (same-origin in practice — no
 *   harm), otherwise leave as-is so test harnesses keep working.
 */
function resolveUrl(path) {
  if (typeof path !== 'string') return path;
  if (/^https?:\/\//i.test(path)) return path; // already absolute
  if (BACKEND_URL) return `${BACKEND_URL}${path}`;
  return path;
}

/**
 * Augment headers with native auth (Bearer + X-Client).
 * Returns the same object on web. Idempotent — safe to call always.
 */
async function withNativeAuth(headers) {
  if (!isNative()) return headers || {};
  const token = await getStoredToken();
  const next = { ...(headers || {}), 'X-Client': 'capacitor' };
  if (token) next.Authorization = `Bearer ${token}`;
  return next;
}

export async function apiGet(path, opts = {}) {
  const { headers: extraHeaders, ...rest } = opts;
  const headers = await withNativeAuth(extraHeaders);
  return fetch(resolveUrl(path), {
    method: 'GET',
    credentials: 'include',
    headers,
    ...rest,
  });
}

export async function apiPost(path, body, opts = {}) {
  const { headers: extraHeaders, ...rest } = opts;
  const isFormData = body instanceof FormData;
  const baseHeaders = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(extraHeaders || {}),
  };
  const headers = await withNativeAuth(baseHeaders);
  return fetch(resolveUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: isFormData ? body : JSON.stringify(body),
    ...rest,
  });
}

export async function apiPut(path, body, opts = {}) {
  const { headers: extraHeaders, ...rest } = opts;
  const headers = await withNativeAuth({
    'Content-Type': 'application/json',
    ...(extraHeaders || {}),
  });
  return fetch(resolveUrl(path), {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
    ...rest,
  });
}

export async function apiPatch(path, body, opts = {}) {
  const { headers: extraHeaders, ...rest } = opts;
  const headers = await withNativeAuth({
    'Content-Type': 'application/json',
    ...(extraHeaders || {}),
  });
  return fetch(resolveUrl(path), {
    method: 'PATCH',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
    ...rest,
  });
}

export async function apiDelete(path, opts = {}) {
  const { headers: extraHeaders, ...rest } = opts;
  const headers = await withNativeAuth(extraHeaders);
  return fetch(resolveUrl(path), {
    method: 'DELETE',
    credentials: 'include',
    headers,
    ...rest,
  });
}

/**
 * Streaming-friendly POST. Identical wiring to apiPost (resolveUrl,
 * withNativeAuth, credentials, JSON body) but returns the raw Response so
 * callers can do `response.body.getReader()` for SSE / streaming reads.
 *
 * Use this only for endpoints that return a Stream, ReadableStream, or
 * SSE — for ordinary JSON, prefer apiPost.
 */
export async function apiStream(path, body, opts = {}) {
  const { headers: extraHeaders, signal, method = 'POST', ...rest } = opts;
  const isFormData = body instanceof FormData;
  const baseHeaders = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(extraHeaders || {}),
  };
  const headers = await withNativeAuth(baseHeaders);
  return fetch(resolveUrl(path), {
    method,
    credentials: 'include',
    headers,
    body: body == null ? undefined : (isFormData ? body : JSON.stringify(body)),
    signal,
    ...rest,
  });
}
